import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faEnvelopeOpen, faClock, faUser, faPaperclip, faChevronRight, faFilter, faSearch, faArrowsRotate, faRobot } from '@fortawesome/free-solid-svg-icons';
import { getEmailList } from '../../services/emailService';
import { FiRefreshCw } from 'react-icons/fi';
import { useToast } from '../common/ToastProvider';
import CoPilotChat from './CoPilotChat';

const ListContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
`;

const ListHeader = styled.div`
  background: white;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  margin: 0;
  color: #333;
  font-size: 1.5rem;
  font-weight: 600;
`;

const HeaderStats = styled.div`
  display: flex;
  gap: 2rem;
  font-size: 0.9rem;
  color: #666;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatLabel = styled.span`
  color: #999;
`;

const StatValue = styled.span`
  font-weight: 600;
  color: #333;
`;

const SearchFilterBar = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const SearchBox = styled.div`
  flex: 1;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #0066FF;
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const FilterButton = styled.button`
  padding: 0.75rem 1.25rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #0066FF;
    color: #0066FF;
  }
`;

const RefreshButton = styled.button`
  padding: 0.75rem 1.25rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #0066FF;
    color: #0066FF;
  }
`;

const TaskListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 2rem;
`;

const TaskCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #0066FF;
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const TaskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const TaskHeaderLeft = styled.div`
  flex: 1;
`;

const TaskId = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #0066FF;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const TaskSubject = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-size: 0.85rem;
  color: #666;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TaskStatus = styled.div`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  
  ${props => {
    switch(props.status) {
      case 'NEW':
        return `
          background: #E3F2FD;
          color: #1976D2;
        `;
      case 'IN_PROGRESS':
        return `
          background: #FFF3E0;
          color: #F57C00;
        `;
      case 'AWAITING_RESPONSE':
        return `
          background: #F3E5F5;
          color: #7B1FA2;
        `;
      case 'RESOLVED':
        return `
          background: #E8F5E9;
          color: #388E3C;
        `;
      default:
        return `
          background: #F5F5F5;
          color: #666;
        `;
    }
  }}
`;

const TaskPreview = styled.div`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-top: 0.75rem;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TaskFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
`;

const AttachmentBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #666;
  background: #f8f9fa;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
`;

const ViewButton = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #0066FF;
  font-size: 0.85rem;
  font-weight: 600;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  color: #ddd;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: #666;
  margin-bottom: 0.5rem;
`;

const EmptyText = styled.div`
  font-size: 0.9rem;
  color: #999;
`;

const FloatingButton = styled.button`
  position: fixed;
  right: 28px;
  bottom: 28px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  // background: linear-gradient(135deg, #90caf9, #cd93d7);
  background: #fff;
  color: white;
  border: none;
  box-shadow:
  0 6px 12px rgba(33, 150, 243, 0.35),
  0 12px 28px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1200;
`;

const FloatingChatWrapper = styled.div`
  position: fixed;
  right: 28px;
  bottom: 96px;
  width: 436px;
  height: calc(100vh - 160px);
  z-index: 1200;
  box-shadow: 0 12px 40px rgba(0,0,0,0.12);
  border-radius: 12px;
  overflow: hidden;
`;

const CloseChatButton = styled.button`
  position: absolute;
  right: 8px;
  top: 8px;
  background: rgba(255,255,255,0.9);
  border: none;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  z-index: 1250;
`;

// [
//    {
//       threadId: 'CLM-402',
//       subject: 'Request to Initiate Insurance Claim – Recent Hospitalisation',
//       status: 'IN_PROGRESS',
//       customerName: 'Nguyen Minh Hoang',
//       customerEmail: 'nm_hoang_987@gmail.com',
//       createdAt: '2025-01-01T09:15:00Z',
//       updatedAt: '2025-01-01T11:30:00Z',
//       messageCount: 2,
//       attachmentCount: 3,
//       preview: 'Dear Team, I am writing to check whether I am currently eligible to receive benefits for my insurance claim...',
//       unread: false
//     }
// ]

const EmailTaskList = ({ onSelectTask }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [tasks, setTasks] = useState([]);
   const { showToast } = useToast();


  useEffect(() => {
    const fetchEmailList = async () => {
      // const resp = await getEmailList('bmm_sl_email_request', {"version": 1});
      const resp = await getEmailList('bmm_sl_email_request', {});
      if(resp.length) {
        setTasks(resp)
      }
    }

    fetchEmailList();
  },[])

  // const formatDate = (dateString) => {
  //   const date = new Date(dateString);
  //   const now = new Date();
  //   const diffTime = Math.abs(now - date);
  //   const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
  //   if (diffDays === 0) {
  //     return date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit' });
  //   } else if (diffDays === 1) {
  //     return 'Yesterday';
  //   } else if (diffDays < 7) {
  //     return `${diffDays} days ago`;
  //   } else {
  //     return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  //   }
  // };
const handleRefresh = async () => {
  try {
    const emails = await getEmailList('bmm_sl_email_request', {});

    if (!emails?.length) {
      showToast({
        message: 'No new emails to refresh.',
        type: 'info',
      });
      return;
    }

    setTasks(emails);

    showToast({
      message: 'Emails refreshed successfully.',
      type: 'success',
    });
  } catch (error) {
    console.error('Error refreshing emails:', error);

    showToast({
      message: 'Failed to refresh emails. Please try again.',
      type: 'error',
    });
  }
};


  const formatDate = (dateString) => {
  if (!dateString) return "";


  let normalizedString = dateString;
  if (!dateString.endsWith('Z') && !dateString.includes('+')) {
    normalizedString = dateString; 
  }

  const date = new Date(normalizedString);

  if (isNaN(date.getTime())) return "Invalid Date";

  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  if (diffTime < 0) {
    return "Just now";
  }
  
  if (diffDays === 0) {
    return date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
};

console.log(formatDate('2025-12-29T07:12:10.415635'))

  const getStatusLabel = (status) => {
    const labels = {
      'NEW': 'New',
      'IN_PROGRESS': 'In Progress',
      'AWAITING_RESPONSE': 'Awaiting Response',
      'RESOLVED': 'Resolved'
    };
    return labels[status] || status;
  };

  const filteredTasks = tasks.filter(task => {
    const firstMsg = (task.messages && task.messages[0]) || {};
    const subject = (task.subject || '').toString().toLowerCase();
    const threadKey = (task.emailThreadId || task.threadId || (task.id ? String(task.id) : '')).toString().toLowerCase();
    const customerName = (firstMsg.customerName || firstMsg.senderName || '').toString().toLowerCase();
    const q = searchQuery.toLowerCase();
    return subject.includes(q) || threadKey.includes(q) || customerName.includes(q);
  });

  const stats = {
    total: tasks.length,
    new: tasks.filter(t => t.status === 'NEW').length,
    inProgress: tasks.filter(t => t.status === 'IN_PROGRESS').length,
    unread: tasks.filter(t => t.unread).length
  };

  return (
    <ListContainer>
      <ListHeader>
        <HeaderTop>
          <Title>Workbench</Title>
          <HeaderStats>
            <StatItem>
              <StatLabel>Total:</StatLabel>
              <StatValue>{stats.total}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>New:</StatLabel>
              <StatValue>{stats.new}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>In Progress:</StatLabel>
              <StatValue>{stats.inProgress}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Unread:</StatLabel>
              <StatValue>{stats.unread}</StatValue>
            </StatItem>
          </HeaderStats>
        </HeaderTop>
        
        <SearchFilterBar>
          <SearchBox>
            <SearchIcon>
              <FontAwesomeIcon icon={faSearch} />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by thread ID, subject, or customer name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBox>
          <FilterButton>
            <FontAwesomeIcon icon={faFilter} />
            Filter
          </FilterButton>
          <RefreshButton onClick={handleRefresh}>
            <FontAwesomeIcon icon={faArrowsRotate} />
            Refresh
          </RefreshButton>
        </SearchFilterBar>
      </ListHeader>

      <TaskListContainer>
        {filteredTasks.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <FontAwesomeIcon icon={faEnvelope} />
            </EmptyIcon>
            <EmptyTitle>No tasks found</EmptyTitle>
            <EmptyText>Try adjusting your search criteria</EmptyText>
          </EmptyState>
        ) : (
          filteredTasks.map(task => {
            const firstMsg = (task.messages && task.messages[0]) || {};

            // Derive display values from new object shape
            const threadKey = task.emailThreadId || task.threadId || (task.id ? String(task.id) : '');
            const updatedAt = task.updatedAt || task.updated_at || task.createdAt || task.created_at;
            const senderName = firstMsg.senderName || firstMsg.customerName || firstMsg.from || '';
            const messageCount = Array.isArray(task.messages) ? task.messages.length : 0;
            const attachmentCount = (firstMsg.attachments && firstMsg.attachments.length) || (task.attachment_info && task.attachment_info.length) || 0;

            // Handle body content which may be plain text or HTML
            const extractBodyContent = (htmlOrText) => {
              if (!htmlOrText) return '';
              // If looks like HTML, return as-is (trusting server-side sanitized HTML)
              if (/<[a-z][\s\S]*>/i.test(htmlOrText)) {
                return htmlOrText;
              }
              // Otherwise escape and convert newlines to <br>
              const escaped = String(htmlOrText)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
              return escaped.replace(/\n/g, '<br/>');
            };

            const bodyContent = extractBodyContent(firstMsg.body || firstMsg.preview || '');

            return (
            <TaskCard key={threadKey} onClick={() => onSelectTask(task)}>
              <TaskHeader>
                <TaskHeaderLeft>
                    {/* Show the email subject but styled like `TaskId` (keeps the TaskId visual style) */}
                    <TaskId>
                      <FontAwesomeIcon icon={task?.unread ? faEnvelope : faEnvelopeOpen} />
                      {task.subject || threadKey}
                    </TaskId>
                  <TaskMeta>
                    <MetaItem>
                      <FontAwesomeIcon icon={faUser} />
                      {senderName}
                    </MetaItem>
                    <MetaItem>
                      <FontAwesomeIcon icon={faClock} />
                      {formatDate(updatedAt)}
                    </MetaItem>
                  </TaskMeta>
                </TaskHeaderLeft>
                <TaskStatus status={task?.status}>
                  {getStatusLabel(task?.status)}
                </TaskStatus>
              </TaskHeader>
              <TaskPreview
               dangerouslySetInnerHTML={{ __html: bodyContent }} 
              ></TaskPreview>

              <TaskFooter>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  {attachmentCount > 0 && (
                    <AttachmentBadge>
                      <FontAwesomeIcon icon={faPaperclip} />
                      {attachmentCount} attachment{attachmentCount > 1 ? 's' : ''}
                    </AttachmentBadge>
                  )}
                  <AttachmentBadge>
                    {messageCount} message{messageCount > 1 ? 's' : ''}
                  </AttachmentBadge>
                </div>
                <ViewButton>
                  View Details
                  <FontAwesomeIcon icon={faChevronRight} />
                </ViewButton>
              </TaskFooter>
            </TaskCard>
            )
          })
        )}
      </TaskListContainer>
      {/* Floating chat button and chat panel */}
      {!showChat && (
        <FloatingButton aria-label="Open chat" onClick={() => setShowChat(true)}>
          {/* <FontAwesomeIcon icon={faRobot} /> */}
          <div
            style={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <img src="/images/logo/meclaims-icon.png" alt="logo" width={32} height={32} />
          </div>
        </FloatingButton>
      )}
      {showChat && (
        <FloatingChatWrapper>
          <CloseChatButton aria-label="Close chat" onClick={() => setShowChat(false)}>✕</CloseChatButton>
          <CoPilotChat
            messages={[]}
            messageLoader={false}
            sessionId={`email-task`}
            initialUserMessage={'Hi'}
            containerStyle={{ height: '100%' }}
          />
        </FloatingChatWrapper>
      )}
    </ListContainer>
  );
};

export default EmailTaskList;
